/**
 * @fileoverview TipTap extension that exposes table-cell styling commands
 * on top of the existing `TableKit` and `PreserveStyles` extensions.
 *
 * Why:
 *   - TableKit handles structure (rows, cells, merging, header toggling).
 *   - PreserveStyles already round-trips the inline `style` attribute on
 *     `table` / `tableRow` / `tableCell` / `tableHeader` to/from the DB.
 *   - This extension wires up the user-facing commands that MUTATE that
 *     `style` string in a structured way, so the toolbar can set
 *     borders, backgrounds, vertical alignment, etc., and have the
 *     changes survive both the editor round-trip AND the Word/PDF
 *     export pipelines (see `prepareForWord` in `exportHtmlDocument.ts`,
 *     which inline-mirrors `border:` to `mso-border-alt:`).
 *
 * Selection semantics:
 *   - Per-cell commands (`setCellBorderSide`, `setCellBackground`,
 *     `setCellVerticalAlign`) apply to every cell in the current
 *     `CellSelection`, or to the single cell at the cursor when no cells
 *     are explicitly selected.
 *   - Whole-table commands (`setTableBorderPreset`, `applyHeaderRowStyle`)
 *     act on the table containing the cursor, regardless of which cell
 *     is selected.
 */

import { Extension } from "@tiptap/core";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { CellSelection, TableMap } from "@tiptap/pm/tables";

import {
  type BorderDecl,
  type BorderSide,
  type StyleMap,
  type VerticalAlign,
  clearAllBorders,
  parseStyle,
  restyleBorders,
  serializeStyle,
  setBackground,
  setBorderSide,
  setVerticalAlign,
} from "./table-style-utils";

// Re-export so toolbar / consumer modules can read these without
// having to know about the underlying utils module.
export type {
  BorderDecl,
  BorderSide,
  VerticalAlign,
} from "./table-style-utils";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableStyle: {
      /**
       * Set (or clear) a single border side on every cell in the
       * current selection. Pass `decl: null` (or `style: "none"`) to
       * remove the side.
       */
      setCellBorderSide: (args: {
        side: BorderSide | "all";
        decl: BorderDecl | null;
      }) => ReturnType;
      /** Background colour for the selected cell(s); `null` clears it. */
      setCellBackground: (color: string | null) => ReturnType;
      /** Vertical alignment for the selected cell(s); `null` resets. */
      setCellVerticalAlign: (align: VerticalAlign | null) => ReturnType;
      /**
       * Apply a border preset to every cell in the current table.
       *   - `"all"`           — border on every side of every cell.
       *   - `"outer"`         — border only on the outside edges.
       *   - `"header-bottom"` — border only under the header row (or
       *                        first row if no explicit header).
       *   - `"none"`          — strip every border, including any
       *                        per-side declarations.
       */
      setTableBorderPreset: (args: {
        preset: "all" | "outer" | "header-bottom" | "none";
        decl?: BorderDecl;
      }) => ReturnType;
      /**
       * Apply a one-click "branded header" look to the first row of
       * the current table — sets background + text colour on every
       * header cell. Useful for matching the Form 535 visual style.
       */
      applyHeaderRowStyle: (args: {
        background: string;
        textColor: string;
        /** When provided, also stamp a border on every cell to keep the table cohesive. */
        cellBorder?: BorderDecl;
      }) => ReturnType;
      /**
       * Update the width and/or colour of every existing border on
       * the current scope, preserving which sides are present.
       *
       * Scope is picked automatically:
       *   - If the user has an explicit CellSelection, only those
       *     cells are restyled.
       *   - Otherwise the WHOLE table containing the cursor is
       *     restyled (this matches the "I picked a new width and
       *     want the table's borders to look different" intent).
       *
       * Returns false when nothing was changed (e.g. the table has
       * no borders yet, so there's nothing to restyle — callers can
       * still update local toolbar state for the next preset apply).
       */
      restyleBordersAuto: (updates: {
        width?: string;
        color?: string;
      }) => ReturnType;
    };
  }
}

type CellInfo = {
  node: ProseMirrorNode;
  pos: number;
};

/** Walk every cell in the current selection (CellSelection or cursor cell). */
function collectSelectedCells(state: EditorState): CellInfo[] {
  const { selection } = state;
  const cells: CellInfo[] = [];

  if (selection instanceof CellSelection) {
    selection.forEachCell((node, pos) => {
      cells.push({ node, pos });
    });
    return cells;
  }

  // No explicit cell selection — fall back to the cell containing the cursor.
  const $cell = findCellAroundPos(state);
  if ($cell) cells.push($cell);
  return cells;
}

/**
 * Walk up from the current cursor looking for a `tableCell` or
 * `tableHeader` ancestor and return it with its document position.
 */
function findCellAroundPos(state: EditorState): CellInfo | null {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
      return { node, pos: $from.before(depth) };
    }
  }
  return null;
}

/** Walk up from the cursor to the surrounding `table` node. */
function findTableAroundPos(
  state: EditorState,
): { node: ProseMirrorNode; pos: number } | null {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "table") {
      return { node, pos: $from.before(depth) };
    }
  }
  return null;
}

/** Collect every cell in the given table, paired with its absolute doc pos. */
function collectTableCells(
  table: ProseMirrorNode,
  tablePos: number,
): Array<CellInfo & { row: number; col: number; map: TableMap }> {
  const map = TableMap.get(table);
  const seen = new Set<number>();
  const cells: Array<CellInfo & { row: number; col: number; map: TableMap }> =
    [];

  for (let row = 0; row < map.height; row++) {
    for (let col = 0; col < map.width; col++) {
      const cellRelPos = map.map[row * map.width + col];
      if (seen.has(cellRelPos)) continue;
      seen.add(cellRelPos);
      const cellNode = table.nodeAt(cellRelPos);
      if (!cellNode) continue;
      // `+ 1` because tablePos points BEFORE the table node; the
      // cell offsets in `map.map` are RELATIVE to the start of the
      // table node's content.
      cells.push({
        node: cellNode,
        pos: tablePos + 1 + cellRelPos,
        row,
        col,
        map,
      });
    }
  }
  return cells;
}

/**
 * Apply `mutator(styleMap)` to every cell in `cells`, writing the
 * updated `style` attribute back via `tr.setNodeMarkup`. Returns the
 * transaction so chained commands keep working.
 */
function mutateCellStyles(
  tr: Transaction,
  cells: CellInfo[],
  mutator: (style: StyleMap, cell: CellInfo) => void,
): Transaction {
  for (const cell of cells) {
    const current = (cell.node.attrs.style as string | null) ?? null;
    const styleMap = parseStyle(current);
    mutator(styleMap, cell);
    const next = serializeStyle(styleMap);
    tr.setNodeMarkup(cell.pos, undefined, {
      ...cell.node.attrs,
      style: next,
    });
  }
  return tr;
}

/**
 * Default border declaration used by the toolbar when the user hasn't
 * picked a colour/width yet. Matches the editor's CSS-only default so
 * "Apply borders" doesn't visually change the editor — it only adds
 * the inline styles that the export pipeline needs.
 */
export const DEFAULT_CELL_BORDER: BorderDecl = {
  width: "0.5pt",
  style: "solid",
  color: "#DCDCDC",
};

export const TableStyleExtension = Extension.create({
  name: "tableStyle",

  addCommands() {
    return {
      setCellBorderSide:
        ({ side, decl }) =>
        ({ state, tr, dispatch }) => {
          const cells = collectSelectedCells(state);
          if (cells.length === 0) return false;
          if (!dispatch) return true;
          mutateCellStyles(tr, cells, (styleMap) => {
            setBorderSide(styleMap, side, decl);
          });
          return true;
        },

      setCellBackground:
        (color) =>
        ({ state, tr, dispatch }) => {
          const cells = collectSelectedCells(state);
          if (cells.length === 0) return false;
          if (!dispatch) return true;
          mutateCellStyles(tr, cells, (styleMap) => {
            setBackground(styleMap, color);
          });
          return true;
        },

      setCellVerticalAlign:
        (align) =>
        ({ state, tr, dispatch }) => {
          const cells = collectSelectedCells(state);
          if (cells.length === 0) return false;
          if (!dispatch) return true;
          mutateCellStyles(tr, cells, (styleMap) => {
            setVerticalAlign(styleMap, align);
          });
          return true;
        },

      setTableBorderPreset:
        ({ preset, decl = DEFAULT_CELL_BORDER }) =>
        ({ state, tr, dispatch }) => {
          const table = findTableAroundPos(state);
          if (!table) return false;
          if (!dispatch) return true;
          const cells = collectTableCells(table.node, table.pos);
          const totalRows = cells[0]?.map.height ?? 0;
          const totalCols = cells[0]?.map.width ?? 0;

          // `noneDecl` writes `border-{side}: none` for inner edges so
          // the editor preview matches the export. Without this the
          // editor's default `border: 1px solid #d1d5db` (set in
          // `editor-styles.css`) shows through and "Outer only" would
          // visually look identical to "All borders".
          const noneDecl: BorderDecl = {
            width: "0",
            style: "none",
            color: "transparent",
          };

          mutateCellStyles(tr, cells, (styleMap, cell) => {
            // Always start from a clean slate so leftover per-side
            // declarations from prior edits don't fight the preset.
            clearAllBorders(styleMap);
            const c = cell as CellInfo & { row: number; col: number };
            switch (preset) {
              case "none":
                setBorderSide(styleMap, "all", noneDecl);
                break;
              case "all":
                setBorderSide(styleMap, "all", decl);
                break;
              case "outer":
                setBorderSide(styleMap, "top", c.row === 0 ? decl : noneDecl);
                setBorderSide(
                  styleMap,
                  "bottom",
                  c.row === totalRows - 1 ? decl : noneDecl,
                );
                setBorderSide(styleMap, "left", c.col === 0 ? decl : noneDecl);
                setBorderSide(
                  styleMap,
                  "right",
                  c.col === totalCols - 1 ? decl : noneDecl,
                );
                break;
              case "header-bottom":
                setBorderSide(styleMap, "all", noneDecl);
                if (c.row === 0) setBorderSide(styleMap, "bottom", decl);
                break;
            }
          });
          return true;
        },

      restyleBordersAuto:
        (updates) =>
        ({ state, tr, dispatch }) => {
          if (updates.width === undefined && updates.color === undefined) {
            return false;
          }
          // CellSelection wins — the user has explicitly scoped their
          // edit. Fall back to whole-table so picking a new colour
          // with the cursor sitting in a single cell still updates
          // every border, matching the "table border" mental model.
          const isCellSel = state.selection instanceof CellSelection;
          const cells = isCellSel
            ? collectSelectedCells(state)
            : tableCellsAtCursor(state);
          if (cells.length === 0) return false;

          if (!dispatch) return true;
          let anyChanged = false;
          mutateCellStyles(tr, cells, (styleMap) => {
            if (restyleBorders(styleMap, updates)) anyChanged = true;
          });
          // Don't mark the command as "ran" when there were no borders
          // to update — keeps undo history clean and lets the toolbar
          // distinguish "no-op" from "applied".
          return anyChanged;
        },

      applyHeaderRowStyle:
        ({ background, textColor, cellBorder }) =>
        ({ state, dispatch, editor }) => {
          const table = findTableAroundPos(state);
          if (!table) return false;
          if (!dispatch) return true;

          // Make sure row 0 is actually a header row. `toggleHeaderRow`
          // already handles the conversion (and is idempotent when the
          // first row IS already a header), so we run it via the editor
          // command chain and then style the resulting cells.
          //
          // We dispatch a separate transaction first via the editor
          // command — this is the cleanest way to interop with
          // TableKit's existing toggle logic and avoids duplicating its
          // structural reshaping here.
          if (!isFirstRowHeader(table.node)) {
            editor.chain().focus().toggleHeaderRow().run();
          }

          // After toggling, the document may have moved — re-find the table.
          const refreshed = findTableAroundPos(editor.state);
          if (!refreshed) return false;
          const allCells = collectTableCells(refreshed.node, refreshed.pos);

          // We need to apply the mutation against the editor's CURRENT
          // transaction, not the stale `tr` we were called with (it
          // doesn't see toggleHeaderRow's changes). Build a fresh one.
          const freshTr = editor.state.tr;
          mutateCellStyles(freshTr, allCells, (styleMap, cell) => {
            const c = cell as CellInfo & { row: number; col: number };
            if (cellBorder) {
              clearAllBorders(styleMap);
              setBorderSide(styleMap, "all", cellBorder);
            }
            if (c.row === 0) {
              setBackground(styleMap, background);
              styleMap.set("color", textColor);
              styleMap.set("font-weight", "600");
            }
          });
          editor.view.dispatch(freshTr);
          return true;
        },
    };
  },
});

/**
 * Returns every cell of the table containing the cursor, or an empty
 * array when the cursor isn't inside a table. Used by the auto-scope
 * border restyle command for the "no explicit CellSelection" case.
 */
function tableCellsAtCursor(state: EditorState): CellInfo[] {
  const table = findTableAroundPos(state);
  if (!table) return [];
  return collectTableCells(table.node, table.pos);
}

/**
 * Returns true when row 0 of `table` is composed entirely of
 * `tableHeader` cells (i.e. the user has already toggled it on).
 */
function isFirstRowHeader(table: ProseMirrorNode): boolean {
  const firstRow = table.firstChild;
  if (!firstRow || firstRow.childCount === 0) return false;
  const cells: ProseMirrorNode[] = [];
  firstRow.forEach((cell) => cells.push(cell));
  return cells.every((cell) => cell.type.name === "tableHeader");
}
